import { Provider } from "src/api/connectApi/types";
import { getProviderName } from "src/connections/ProviderResourceProperties";
import {
  AddNodeGroupType,
  ConnectionGroupType,
  ConnectNodeType,
  CreatableGroupedNodeType,
  NamedNodeType,
  NODE_TYPE,
  SimpleCreatableNodeType,
} from "src/constants/NodeTypes";
import { assertUnreachable } from "src/utils/typeUtils";

type NodeProperty = {
  ADD_BUTTON_DESCRIPTION: string;
  DOCS_HELP_BUTTON?: string;
  IS_BETA?: boolean;
  INFO_TITLE?: React.ReactNode;
  INFO_BODY?: React.ReactNode;
};

type NodeGroupProperty = NodeProperty & {
  HEADER: string;
};

export const CREATABLE_NODE_PROPERTIES: {
  [key in SimpleCreatableNodeType | CreatableGroupedNodeType]: NodeProperty;
} = {
  ai_node_v2: {
    ADD_BUTTON_DESCRIPTION: "Use advanced AI models in your decision flow",
  },
  agent_node: {
    ADD_BUTTON_DESCRIPTION: "Use AI agents to process complex tasks",
  },
  code_node: {
    ADD_BUTTON_DESCRIPTION: "Transform data with Python code",
  },
  split_node_v2: {
    ADD_BUTTON_DESCRIPTION: "Split data based on conditions",
  },
  rule_node_v2: {
    ADD_BUTTON_DESCRIPTION: "Set values based on conditions",
  },
  ml_node: {
    ADD_BUTTON_DESCRIPTION: "Make predictions using machine learning",
  },
  decision_table_node: {
    ADD_BUTTON_DESCRIPTION:
      "Express multi-conditions with multiple results per case",
    DOCS_HELP_BUTTON:
      "https://docs.taktile.com/decision-flow-fundamentals/node-types/decision-table-node",
    INFO_TITLE: "Use Decision Table when:",
    INFO_BODY: (
      <>
        <ul className="list-inside list-disc">
          <li>You have more than two inputs</li>
          <li>You need to return multiple outputs</li>
          <li>You want full control over rule conditions</li>
        </ul>
        <a
          className="text-white underline font-inter-semibold-13px"
          href="https://docs.taktile.com/decision-flow-fundamentals/node-types/decision-table-node"
          rel="noopener noreferrer"
          target="_blank"
        >
          See example
        </a>
      </>
    ),
  },
  matrix_table_node: {
    ADD_BUTTON_DESCRIPTION: "Define a result based on two conditions",
    INFO_TITLE: "Use 2D Matrix when:",
    DOCS_HELP_BUTTON:
      "https://docs.taktile.com/decision-flow-fundamentals/node-types/2d-matrix-node",
    INFO_BODY: (
      <>
        <ul className="list-inside list-disc">
          <li>You have exactly two inputs</li>
          <li>You need a single output per combination</li>
          <li>You want a compact, visual grid</li>
        </ul>
        <a
          className="text-white underline font-inter-semibold-13px"
          href="https://docs.taktile.com/decision-flow-fundamentals/node-types/2d-matrix-node"
          rel="noopener noreferrer"
          target="_blank"
        >
          See example
        </a>
      </>
    ),
  },
  assignment_node: {
    ADD_BUTTON_DESCRIPTION: "Assign, update, or calculate values",
  },
  scorecard_node: {
    ADD_BUTTON_DESCRIPTION: "Calculate score from weighted factors",
    DOCS_HELP_BUTTON:
      "https://docs.taktile.com/decision-flow-fundamentals/node-types/scorecard-node",
  },
  flow_node: {
    ADD_BUTTON_DESCRIPTION: "Call a Decision Flow version in your workspace",
  },
  loop_node: {
    ADD_BUTTON_DESCRIPTION: "Apply a Decision Flow to every item in a list",
  },
  read_entity_node: {
    ADD_BUTTON_DESCRIPTION: "Read an Entity from the Entity table",
  },
  write_entity_node: {
    ADD_BUTTON_DESCRIPTION: "Create or update an Entity on Entity table",
  },
} as const;

export const ADD_NODE_GROUP_PROPERTIES: {
  [key in AddNodeGroupType]: NodeGroupProperty;
} = {
  [AddNodeGroupType.ENTITY_ACTIONS]: {
    ADD_BUTTON_DESCRIPTION: "Create, read, or update an Entity",
    HEADER: "Choose the action",
  },
};

export const CONNECTION_GROUP_PROPERTIES: {
  [key in ConnectionGroupType]: NodeGroupProperty;
} = {
  [ConnectionGroupType.CASE_ACTIONS]: {
    ADD_BUTTON_DESCRIPTION: "Create or update a Case",
    HEADER: "Choose the action",
  },
};

type GetNodeTitleParams =
  | {
      nodeType: Exclude<
        NamedNodeType,
        | NODE_TYPE.INTEGRATION_NODE
        | NODE_TYPE.MANIFEST_CONNECTION_NODE
        | NODE_TYPE.SQL_DATABASE_CONNECTION_NODE
      >;
    }
  | {
      nodeType: ConnectNodeType;
      provider: Provider;
    }
  | {
      nodeType: AddNodeGroupType;
    }
  | {
      nodeType: ConnectionGroupType;
    };

export const getNodeTitle = (params: GetNodeTitleParams): string => {
  switch (params.nodeType) {
    case NODE_TYPE.SQL_DATABASE_CONNECTION_NODE:
    case NODE_TYPE.INTEGRATION_NODE: {
      return getProviderName(params.provider);
    }
    case NODE_TYPE.AI_NODE_V2: {
      return "AI";
    }
    case NODE_TYPE.AGENT_NODE: {
      return "Agent";
    }
    case NODE_TYPE.INPUT_NODE: {
      return "Input";
    }
    case NODE_TYPE.CODE_NODE: {
      return "Code";
    }
    case NODE_TYPE.OUTPUT_NODE: {
      return "Output";
    }
    case NODE_TYPE.SPLIT_BRANCH_NODE_V2: {
      return "Branch";
    }
    case NODE_TYPE.SPLIT_NODE_V2: {
      return "Split";
    }
    case NODE_TYPE.RULE_NODE_V2: {
      return "Rule";
    }
    case NODE_TYPE.ML_NODE: {
      return "Machine Learning";
    }
    case NODE_TYPE.CUSTOM_CONNECTION_NODE: {
      return "Custom Connection";
    }
    case NODE_TYPE.WEBHOOK_CONNECTION_NODE: {
      return "Inbound Webhook";
    }
    case NODE_TYPE.DECISION_TABLE_NODE: {
      return "Decision Table";
    }
    case NODE_TYPE.MATRIX_TABLE_NODE: {
      return "2D Matrix";
    }
    case NODE_TYPE.ASSIGNMENT_NODE: {
      return "Assignment";
    }
    case NODE_TYPE.SCORECARD_NODE: {
      return "Scorecard";
    }
    case NODE_TYPE.FLOW_NODE: {
      return "Decision Flow";
    }
    case NODE_TYPE.REVIEW_CONNECTION_NODE: {
      return "Manual Review";
    }
    case NODE_TYPE.LOOP_NODE: {
      return "Loop";
    }
    case NODE_TYPE.CASE_CREATION_NODE: {
      return "Create Case";
    }
    case NODE_TYPE.CASE_UPDATE_NODE: {
      return "Update Case";
    }
    case NODE_TYPE.READ_ENTITY_NODE: {
      return "Read Entity";
    }
    case NODE_TYPE.WRITE_ENTITY_NODE: {
      return "Create or update Entity";
    }
    case AddNodeGroupType.ENTITY_ACTIONS: {
      return "Entity actions";
    }
    case ConnectionGroupType.CASE_ACTIONS: {
      return "Case actions";
    }
    default:
      assertUnreachable(params);
      return "";
  }
};
